// Image optimization and lazy loading for Pet Breed Matcher

/**
 * Handles lazy loading of images and optimizations
 */
class ImageOptimizer {
  constructor() {
    this.images = document.querySelectorAll('.lazy-load');
    this.init();
  }

  init() {
    // Set up intersection observer for lazy loading
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            this.loadImage(img);
            observer.unobserve(img);
          }
        });
      });

      this.images.forEach(image => {
        imageObserver.observe(image);
      });
    } else {
      // Fallback for browsers that don't support IntersectionObserver
      this.images.forEach(image => {
        this.loadImage(image);
      });
    }

    // Add event listener for window resize to handle responsive images
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  loadImage(img) {
    const src = img.getAttribute('data-src');
    if (!src) return;

    // Add loading class
    img.parentElement.classList.add('image-loading');

    // Create a new image to preload
    const preloadImg = new Image();
    preloadImg.src = src;
    
    preloadImg.onload = () => {
      // Once loaded, update the actual image
      img.src = src;
      img.classList.add('loaded');
      img.parentElement.classList.remove('image-loading');
    };

    preloadImg.onerror = () => {
      // Handle error - show placeholder
      img.parentElement.classList.remove('image-loading');
      
      // Determine if it's a dog or cat image based on class or data attribute
      if (img.classList.contains('dog-image') || img.getAttribute('data-pet-type') === 'dog') {
        img.parentElement.classList.add('dog-placeholder', 'dog-silhouette');
      } else if (img.classList.contains('cat-image') || img.getAttribute('data-pet-type') === 'cat') {
        img.parentElement.classList.add('cat-placeholder', 'cat-silhouette');
      } else {
        img.parentElement.classList.add('dog-placeholder'); // Default fallback
      }
    };
  }

  handleResize() {
    // Check window width to determine which image size to use
    const windowWidth = window.innerWidth;
    
    // Get all responsive images that have multiple sources
    const responsiveImages = document.querySelectorAll('[data-src-small], [data-src-medium], [data-src-large]');
    
    responsiveImages.forEach(img => {
      let newSrc;
      
      // Determine appropriate image size based on screen width
      if (windowWidth <= 480) {
        newSrc = img.getAttribute('data-src-small');
      } else if (windowWidth <= 768) {
        newSrc = img.getAttribute('data-src-medium') || img.getAttribute('data-src-large');
      } else {
        newSrc = img.getAttribute('data-src-large');
      }
      
      // Update image source if it's different from current
      if (newSrc && img.src !== newSrc && img.classList.contains('loaded')) {
        img.src = newSrc;
      }
    });
  }
}

// Initialize image optimizer when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new ImageOptimizer();
});

// Export for use in other scripts
export default ImageOptimizer;
